import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation } from '../../../Api/ApiMethods';
import PropTypes from 'prop-types';

const ibanLengths = {
    AL: 28, AD: 24, AT: 20, AZ: 28, BH: 22, BE: 16, BA: 20, BR: 29, BG: 22,
    CR: 22, HR: 21, CY: 28, CZ: 24, DK: 18, DO: 28, EE: 20, FO: 18, FI: 18,
    FR: 27, GE: 22, DE: 22, GI: 23, GR: 27, GL: 18, GT: 28, HU: 28, IS: 26,
    IE: 22, IL: 23, IT: 27, JO: 30, KZ: 20, KW: 30, LV: 21, LB: 28, LI: 21,
    LT: 20, LU: 20, MT: 31, MR: 27, MU: 30, MD: 24, MC: 27, ME: 22, NL: 18,
    MK: 19, NO: 15, PK: 24, PS: 29, PL: 28, PT: 25, QA: 29, RO: 24, SM: 27,
    SA: 24, RS: 22, SK: 24, SI: 19, ES: 24, SE: 24, CH: 21, TN: 24, TR: 26,
    AE: 23, GB: 22, VG: 24,
};

const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;

const InsuranceCompanyDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedCompany, refreshData }) => {
    const initialCompanyData = {
        id: '',
        name: '',
        address: '',
        phoneNumber: '',
        email: '',
        contactPerson: '',
        website: '',
        taxNumber: '',
        ibanNumber: '',
        isArbitrationMember: false,
    };

    const [companyData, setCompanyData] = useState(initialCompanyData);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedCompany) {
                setCompanyData({ 
                    ...initialCompanyData, 
                    ...selectedCompany,
                    isArbitrationMember: selectedCompany.isArbitrationMember === true || selectedCompany.isArbitrationMember === 'yes' 
                                        ? 'yes' 
                                        : 'no' 
                });
            } else {
                resetForm();
            }
            setErrors({});
        }
    }, [mode, selectedCompany, isOpen]);    

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        const fieldsToUppercase = ['name', 'address', 'contactPerson'];

        if (fieldsToUppercase.includes(name) && typeof processedValue === 'string') {
            processedValue = processedValue.toLocaleUpperCase('tr-TR');
        }
        else if (name === 'email' && typeof processedValue === 'string') {
            processedValue = processedValue.toLowerCase();
        }
        else if (name === 'phoneNumber' && typeof processedValue === 'string') {
            processedValue = processedValue.replace(/[^0-9]/g, '');
            if (processedValue.length > 11) {
                processedValue = processedValue.slice(0, 11);
            }
        }
        else if (name === 'taxNumber' && typeof processedValue === 'string') {
            processedValue = processedValue.replace(/[^0-9]/g, '');
            if (processedValue.length > 10) {
                processedValue = processedValue.slice(0, 10);
            }
        }
        else if (name === 'ibanNumber' && typeof processedValue === 'string') {
            processedValue = processedValue.toUpperCase().replace(/\s/g, '');
        }

        setCompanyData(prevData => ({ ...prevData, [name]: processedValue }));

        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        }
    };

    const validateForm = () => {
        const validationErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const turkishTaxNumberRegex = /^\d{10}$/;
        const turkishPhoneRegex = /^05\d{9}$/;
        const websiteRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;

        if (!companyData.name?.trim()) {
            validationErrors.name = 'Şirket İsmi zorunludur.';
        } else if (companyData.name.trim().length < 2) {
            validationErrors.name = 'Şirket İsmi en az 2 karakter olmalıdır.';
        }

        if (companyData.address && companyData.address.trim().length < 5) {
            validationErrors.address = 'Adres en az 5 karakter olmalıdır.';
        }

        if (!companyData.phoneNumber?.trim()) {
            validationErrors.phoneNumber = 'Telefon Numarası zorunludur.';
        } else if (!turkishPhoneRegex.test(companyData.phoneNumber)) {
            validationErrors.phoneNumber = 'Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır.';
        }

        if (!companyData.email?.trim()) {
            validationErrors.email = 'E-posta zorunludur.';
        } else if (!emailRegex.test(companyData.email)) {
            validationErrors.email = 'Geçerli bir e-posta adresi giriniz.';
        }

        if (!companyData.contactPerson?.trim()) {
            validationErrors.contactPerson = 'İletişim Kişisi zorunludur.';
        } else if (companyData.contactPerson.trim().length < 2) {
            validationErrors.contactPerson = 'İletişim Kişisi en az 2 karakter olmalıdır.';
        }

        if (companyData.website?.trim() && !websiteRegex.test(companyData.website)) {
            validationErrors.website = 'Geçerli bir web sitesi adresi giriniz (örn: https://site.com veya site.com).';
        }

        if (companyData.taxNumber?.trim() && !turkishTaxNumberRegex.test(companyData.taxNumber)) {
            validationErrors.taxNumber = 'Vergi Numarası 10 haneli rakam olmalıdır.';
        }

        if (companyData.ibanNumber?.trim()) {
            const countryCode = companyData.ibanNumber.slice(0, 2);
            if (!ibanRegex.test(companyData.ibanNumber)) {
                validationErrors.ibanNumber = 'Geçerli bir IBAN formatı giriniz (örn: TR12...).';
            } else if (ibanLengths[countryCode] && companyData.ibanNumber.length !== ibanLengths[countryCode]) {
                validationErrors.ibanNumber = `IBAN uzunluğu ${ibanLengths[countryCode]} karakter olmalıdır (${countryCode}).`;
            } else if (!ibanLengths[countryCode]) {
                console.warn(`IBAN length check not available for country code: ${countryCode}`);
            }
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const resetForm = () => {
        setCompanyData(initialCompanyData);
        setErrors({});
    };

    const handleCancel = () => {
        resetForm();
        toggle();
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toast.error('Lütfen formdaki hataları düzeltin.');
            const firstErrorKey = Object.keys(errors).find(key => errors[key]);
            if (firstErrorKey) document.getElementById(firstErrorKey)?.focus();
            return;
        }

        const payload = {
            name: companyData.name || '',
            address: companyData.address || null,
            phoneNumber: companyData.phoneNumber || '',
            email: companyData.email || '',
            contactPerson: companyData.contactPerson || '',
            website: companyData.website || null,
            taxNumber: companyData.taxNumber || null,
            ibanNumber: companyData.ibanNumber || null,
            isArbitrationMember: companyData.isArbitrationMember === 'yes',
            ...(mode === 'modify' && selectedCompany?.id && { id: selectedCompany.id })
        };

        console.log("Sending Payload:", payload);

        try {
            await handleAddModifyOperation({
                apiEndpoint,
                selectedItem: mode === 'modify' ? { id: selectedCompany?.id } : null,
                mode,
                data: payload,
                refreshDataCallback: refreshData,
                componentName: 'Sigorta Şirketi',
            });
            toggle();
        } catch (error) {
            console.error("Sigorta şirketi kaydetme hatası:", error);
        }
    };

    const getModalTitle = () => {
        return mode === 'add' ? 'Sigorta Şirketi Ekle' : 'Sigorta Şirketi Düzenle';
    };

    const getButtonLabel = () => {
        return mode === 'add' ? 'Ekle' : 'Güncelle';
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="name">Şirket İsmi <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="name"
                            name="name"
                            value={companyData.name || ''}
                            onChange={handleInputChange}
                            placeholder="Sigorta Şirketinin Tam Adı"
                            invalid={!!errors.name}
                        />
                        <FormFeedback>{errors.name}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="address">Adres</Label>
                        <Input
                            type="text"
                            id="address"
                            name="address"
                            value={companyData.address || ''}
                            onChange={handleInputChange}
                            placeholder="Şirketin Adresi (Opsiyonel)"
                            invalid={!!errors.address}
                        />
                        <FormFeedback>{errors.address}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="phoneNumber">Telefon Numarası <span className="text-danger">*</span></Label>
                        <Input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={companyData.phoneNumber || ''}
                            onChange={handleInputChange}
                            placeholder="05XX XXX XX XX"
                            maxLength={11}
                            invalid={!!errors.phoneNumber}
                        />
                        <FormFeedback>{errors.phoneNumber}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="email">E-posta Adresi <span className="text-danger">*</span></Label>
                        <Input
                            type="email"
                            id="email"
                            name="email"
                            value={companyData.email || ''}
                            onChange={handleInputChange}
                            placeholder="iletisim@sirket.com"
                            invalid={!!errors.email}
                        />
                        <FormFeedback>{errors.email}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="contactPerson">İletişim Kişisi <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="contactPerson"
                            name="contactPerson"
                            value={companyData.contactPerson || ''}
                            onChange={handleInputChange}
                            placeholder="Yetkili Kişinin Adı Soyadı"
                            invalid={!!errors.contactPerson}
                        />
                        <FormFeedback>{errors.contactPerson}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="website">Web Sitesi</Label>
                        <Input
                            type="url"
                            id="website"
                            name="website"
                            value={companyData.website || ''}
                            onChange={handleInputChange}
                            placeholder="https://www.sirket.com (Opsiyonel)"
                            invalid={!!errors.website}
                        />
                        <FormFeedback>{errors.website}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="taxNumber">Vergi Numarası</Label>
                        <Input
                            type="text"
                            id="taxNumber"
                            name="taxNumber"
                            value={companyData.taxNumber || ''}
                            onChange={handleInputChange}
                            placeholder="10 Haneli Vergi Numarası (Opsiyonel)"
                            maxLength={10}
                            invalid={!!errors.taxNumber}
                        />
                        <FormFeedback>{errors.taxNumber}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="ibanNumber">IBAN Numarası</Label>
                        <Input
                            type="text"
                            id="ibanNumber"
                            name="ibanNumber"
                            value={companyData.ibanNumber || ''}
                            onChange={handleInputChange}
                            placeholder="TRXX XXXX XXXX XXXX XXXX XXXX XX (Opsiyonel)"
                            invalid={!!errors.ibanNumber}
                        />
                        <FormFeedback>{errors.ibanNumber}</FormFeedback>
                    </FormGroup>
                    <FormGroup className="mb-3">
                        <Label for="isArbitrationMemberRadio" className="text-nowrap mb-0">
                            Tahkime Üye Mi? {/* Add required span if necessary: <span className="text-danger">*</span> */}
                        </Label>
                        <div className="d-flex mt-2">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="isArbitrationMemberTrue"
                                    type="radio"
                                    name="isArbitrationMember"
                                    value="yes"
                                    checked={companyData.isArbitrationMember === "yes"}
                                    onChange={() =>
                                        setCompanyData((prevFormData) => ({
                                            ...prevFormData,
                                            isArbitrationMember: "yes",
                                        }))
                                    }
                                    // invalid={!!errors.isArbitrationMember} // Add if validation is needed
                                />
                                <Label for="isArbitrationMemberTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="isArbitrationMemberFalse"
                                    type="radio"
                                    name="isArbitrationMember"
                                    value="no"
                                    checked={companyData.isArbitrationMember === "no" || companyData.isArbitrationMember === false || companyData.isArbitrationMember === null}
                                    onChange={() =>
                                        setCompanyData((prevFormData) => ({
                                            ...prevFormData,
                                            isArbitrationMember: "no",
                                        }))
                                    }
                                    // invalid={!!errors.isArbitrationMember} // Add if validation is needed
                                />
                                <Label for="isArbitrationMemberFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        {/* <FormFeedback style={{ display: errors.isArbitrationMember ? 'block' : 'none' }}>{errors.isArbitrationMember}</FormFeedback> // Add if validation is needed */}
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleSave}>
                    {getButtonLabel()}
                </Button>
                <Button color="secondary" onClick={handleCancel} className="ms-2">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

InsuranceCompanyDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedCompany: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
};

export default InsuranceCompanyDataModal;
